import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import { Inline, xcss } from '@atlaskit/primitives';
import Button from '@atlaskit/button/new';

import { useSetErrorMessage } from './providers/AiBulkNavigationContextProvider';
import { useCreateAllIssues } from './utils/useCreateAllIssues';
import { useBulkCreateContext } from './providers/BulkCreateContextProvider';
import { useBulkCreateFlags } from './utils/useBulkCreateFlags';
import { useBulkCreateLoggingEvents } from './utils/useBulkCreateLoggingEvents';
import { messages } from './messages';
import {
	bulkConstructSmartlinkTags,
	constructHeading,
	constructJiraIssueUrl,
	insertContentAtSelectionEnd,
	refreshPageWithTimeout,
} from './utils/issueInsertionUtils';
import type { CreateIssueResponse } from './__types__/apiUtils';

const buttonGroupStyles = xcss({
	paddingTop: 'space.050',
});

// Bulk create button group - used for both AI and non-AI bulk create
export const BulkCreateButtonGroup = ({ onClose }: { onClose: () => void }) => {
	const { formatMessage } = useIntl();
	const { createAllIssuesHandler, loading: isCreateAllLoading } = useCreateAllIssues();
	const [
		{
			aiBulkIssuesList,
			issueTypeHasRequiredFields,
			currentJiraSite,
			insertStorageFragmentRequestPayload,
		},
		{ removeAiBulkIssueList },
	] = useBulkCreateContext();
	const {
		triggerMultipleIssuesCreatedFlag,
		triggerSingleIssueInsertLinkFailureFlag,
		triggerMultipleIssuesInsertLinkFailureFlag,
	} = useBulkCreateFlags();
	const setErrorMessage = useSetErrorMessage();
	const { logCreateAllClick, logMultipleIssueCreateSuccess, logAiIssueCreationFailed } =
		useBulkCreateLoggingEvents();

	const constructXmlModification = useCallback(
		(createdIssues, insertStorageFragmentRequestPayload) => {
			const smartlinkTags = bulkConstructSmartlinkTags(
				createdIssues.map((issue: CreateIssueResponse) =>
					constructJiraIssueUrl(currentJiraSite?.url, issue.key),
				),
			);
			const headingString = formatMessage(messages.bulkCreateIssueInsertionHeading);
			return insertStorageFragmentRequestPayload.selectedText !== headingString
				? // This is just how the backend likes it. Otherwise, the insertion will miss the issue links.
					`${constructHeading(headingString)}<br/>${smartlinkTags}`
				: `<p>${smartlinkTags}</p>`;
		},
		[currentJiraSite?.url, formatMessage],
	);

	const onCreateAllClick = useCallback(() => {
		logCreateAllClick();
		setErrorMessage(null);
		void createAllIssuesHandler({
			onComplete: ({ createdIssues, createdIssueIDs, failedIssueIDs }) => {
				logMultipleIssueCreateSuccess(createdIssueIDs.length);
				if (insertStorageFragmentRequestPayload) {
					insertStorageFragmentRequestPayload.xmlModification = constructXmlModification(
						createdIssues,
						insertStorageFragmentRequestPayload,
					);
					insertContentAtSelectionEnd(insertStorageFragmentRequestPayload)
						.then((data) => {
							// There is a case where the API response is OK, but the response data is false.
							// When this happens, the backend has trouble inserting the ticket to the page and we should show an error toast.
							if (data === false) {
								throw new Error('Failed to insert storage fragment');
							}
						})
						.then(() => {
							refreshPageWithTimeout();
						})
						.catch((_error) => {
							if (createdIssues.length === 1) {
								triggerSingleIssueInsertLinkFailureFlag({
									issueKey: createdIssues[0].key,
									summary: createdIssues[0].summary,
								});
							} else {
								triggerMultipleIssuesInsertLinkFailureFlag(createdIssues);
							}
						});
				}
				void removeAiBulkIssueList(createdIssueIDs);
				triggerMultipleIssuesCreatedFlag(createdIssues);
				if (failedIssueIDs.length !== 0) {
					logAiIssueCreationFailed(failedIssueIDs.length);
					void setErrorMessage({
						createdCount: createdIssueIDs.length,
						failedCount: failedIssueIDs.length,
					});
				}
			},
			onFailure: (error: Error | unknown) => {
				logAiIssueCreationFailed(aiBulkIssuesList.length, error);
				void setErrorMessage({ createdCount: 0, failedCount: aiBulkIssuesList.length });
			},
		});
	}, [
		logCreateAllClick,
		setErrorMessage,
		createAllIssuesHandler,
		logMultipleIssueCreateSuccess,
		insertStorageFragmentRequestPayload,
		removeAiBulkIssueList,
		triggerMultipleIssuesCreatedFlag,
		constructXmlModification,
		triggerSingleIssueInsertLinkFailureFlag,
		triggerMultipleIssuesInsertLinkFailureFlag,
		logAiIssueCreationFailed,
		aiBulkIssuesList.length,
	]);

	return (
		<Inline space="space.150" xcss={buttonGroupStyles}>
			<Button appearance="subtle" onClick={onClose} testId="bulk-create-button-group-cancel">
				<FormattedMessage {...messages.bulkCreateCancelFooterButton} />
			</Button>
			<Button
				appearance="primary"
				isDisabled={issueTypeHasRequiredFields}
				isLoading={isCreateAllLoading}
				onClick={onCreateAllClick}
				testId="bulk-create-button-group-create-all"
			>
				<FormattedMessage {...messages.bulkCreateCreateAllFooterButton} />
			</Button>
		</Inline>
	);
};
